import * as React from "react";

/**
 * Theme management for the dashboard.
 *
 * Tokens live in `dashboard/src/styles/tokens.css` and switch on the
 * `data-theme` attribute set on `<html>`. Recipe documented in the
 * root `DESIGN.md` and `Docs/design/02-tokens.md`.
 *
 * The chosen mode is persisted to `localStorage["omni:theme"]` and
 * applied synchronously by the boot script in `index.html` to prevent
 * a flash of incorrect theme before React hydrates.
 */

export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "omni:theme";

interface ThemeContextValue {
    mode: ThemeMode;
    resolved: ResolvedTheme;
    setMode: (next: ThemeMode) => void;
    cycle: () => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function readStoredMode(): ThemeMode {
    if (typeof window === "undefined") return "system";
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
    return "system";
}

function systemPrefersDark(): boolean {
    if (typeof window === "undefined") return false;
    if (typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(mode: ThemeMode): ResolvedTheme {
    if (mode === "system") return systemPrefersDark() ? "dark" : "light";
    return mode;
}

function applyTheme(resolved: ResolvedTheme): void {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", resolved);
    document.documentElement.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [mode, setModeState] = React.useState<ThemeMode>(() => readStoredMode());
    const [resolved, setResolved] = React.useState<ResolvedTheme>(() => resolve(readStoredMode()));

    React.useEffect(() => {
        const next = resolve(mode);
        setResolved(next);
        applyTheme(next);
        if (typeof window !== "undefined") {
            window.localStorage.setItem(THEME_STORAGE_KEY, mode);
        }
    }, [mode]);

    React.useEffect(() => {
        if (mode !== "system" || typeof window === "undefined") return;
        if (typeof window.matchMedia !== "function") return;
        const mql = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = () => {
            const next: ResolvedTheme = mql.matches ? "dark" : "light";
            setResolved(next);
            applyTheme(next);
        };
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, [mode]);

    const value = React.useMemo<ThemeContextValue>(() => {
        const setMode = (next: ThemeMode) => setModeState(next);
        const cycle = () =>
            setModeState((prev) =>
                prev === "system" ? "light" : prev === "light" ? "dark" : "system",
            );
        return { mode, resolved, setMode, cycle };
    }, [mode, resolved]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
    const ctx = React.useContext(ThemeContext);
    if (!ctx) {
        throw new Error("useTheme must be used inside <ThemeProvider>");
    }
    return ctx;
}
